import { ChangeDetectionStrategy, Component, computed, inject, input, model, output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  CalendarEvent,
  CalendarEventCategory,
  CalendarSettings,
  CalendarSource,
  CalendarView,
  DEFAULT_CALENDAR_SETTINGS,
} from '../../models/calendar.types';
import { CalendarExportService } from '../../services/calendar-export.service';
import { formatMonthTitle, formatWeekTitle } from '../../utils/calendar-date.utils';

@Component({
  selector: 'aura-calendar-header',
  imports: [
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatSelectModule,
  ],
  templateUrl: './calendar-header.component.html',
  styles: [
    `
      :host ::ng-deep .aura-calendar-view-select .mat-mdc-text-field-wrapper {
        padding-top: 0;
        padding-bottom: 0;
        height: 36px;
      }
      :host ::ng-deep .aura-calendar-view-select .mat-mdc-form-field-flex {
        height: 36px;
        align-items: center;
      }
      :host ::ng-deep .aura-calendar-view-select .mat-mdc-select-trigger {
        font-size: 0.75rem;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CalendarHeaderComponent {
  private readonly exportService = inject(CalendarExportService);

  readonly currentDate = input.required<Date>();
  readonly view = input.required<CalendarView>();
  readonly events = input<CalendarEvent[]>([]);
  readonly sources = model<CalendarSource[]>([]);
  readonly settings = input<CalendarSettings>(DEFAULT_CALENDAR_SETTINGS);

  readonly previous = output<void>();
  readonly next = output<void>();
  readonly today = output<void>();
  readonly viewChange = output<CalendarView>();
  readonly sourceToggle = output<string>();
  readonly openSettings = output<void>();

  readonly title = computed(() => {
    const v = this.view();
    const d = this.currentDate();
    const s = this.settings();
    if (v === 'week') {
      return formatWeekTitle(d, 'fr-FR', s.firstDayOfWeek, s.showWeekends);
    }
    return formatMonthTitle(d);
  });

  onExportICal(): void {
    const evts = this.events();
    if (evts.length > 0) {
      this.exportService.downloadICalFile(evts);
    }
  }

  readonly selectedSourceIds = computed(() =>
    this.sources()
      .filter((s) => s.visible)
      .map((s) => s.id),
  );

  onSourcesSelectionChange(selectedIds: string[]): void {
    const set = new Set(selectedIds);
    const updated = this.sources().map((s: CalendarSource) => ({
      ...s,
      visible: set.has(s.id),
    }));
    this.sources.set(updated);
  }

  onToggleSource(sourceId: string, event: MouseEvent): void {
    event.stopPropagation();
    const updated = this.sources().map((s: CalendarSource) =>
      s.id === sourceId ? { ...s, visible: !s.visible } : s,
    );
    this.sources.set(updated);
    this.sourceToggle.emit(sourceId);
  }

  getSourceDotClass(category?: CalendarEventCategory): string {
    switch (category) {
      case 'jade':
        return 'bg-jade-9';
      case 'amber':
        return 'bg-amber-9';
      case 'ruby':
        return 'bg-ruby-9';
      case 'violet':
        return 'bg-violet-9';
      case 'indigo':
        return 'bg-indigo-9';
      case 'primary':
      default:
        return 'bg-primary-9';
    }
  }
}
